import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PlayerStatsService } from '@/services/player-stats';
import { PlayerRanking, PlayerActivity, PlayerStats } from '@/types/stats';
import { useLanguage } from '@/hooks/use-language';
import { Leaderboard } from '@/components/admin/Leaderboard';
import { PlayerDrawer } from '@/components/admin/PlayerDrawer';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { format, formatDistanceToNow } from 'date-fns';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

const service = new PlayerStatsService();

// Helper function to convert PlayerRanking to PlayerStats for the drawer
function convertToPlayerStats(ranking: PlayerRanking): PlayerStats {
  const winRate = ranking.matches_played > 0 ? 0.5 : 0; // Default 50% if no data
  const confidenceInterval: [number, number] = [
    ranking.rating - 1.96 * ranking.rating_deviation,
    ranking.rating + 1.96 * ranking.rating_deviation
  ];
  
  // Estimate recent form based on rating delta
  const recentForm: 'up' | 'down' | 'stable' = 
    ranking.last_rating_delta > 5 ? 'up' :
    ranking.last_rating_delta < -5 ? 'down' : 'stable';
  
  return {
    id: ranking.player_id,
    name: ranking.name,
    phone: ranking.phone,
    rating: ranking.rating,
    rating_deviation: ranking.rating_deviation,
    volatility: 0.06, // Default volatility
    reliability: ranking.reliability,
    level: ranking.level,
    games_played: ranking.matches_played,
    wins: Math.round(ranking.matches_played * winRate),
    losses: Math.round(ranking.matches_played * (1 - winRate)),
    last_match_at: ranking.last_activity_at || undefined,
    preferred_name: undefined,
    member_id: ranking.member_id || undefined,
    created_at: new Date().toISOString(), // Default to now
    updated_at: new Date().toISOString(),
    win_rate: winRate,
    confidence_interval: confidenceInterval,
    level_estimate: ranking.level,
    recent_form: recentForm
  };
}

export default function Rankings() {
  const { t, language } = useLanguage();
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerRanking | null>(null);
  const [drawerPlayer, setDrawerPlayer] = useState<PlayerStats | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data: players = [] } = useQuery<PlayerRanking[]>({
    queryKey: ['player-rankings'],
    queryFn: () => service.getRankings(),
  });

  const { data: activity = [] } = useQuery<PlayerActivity[]>({
    queryKey: ['recent-activity', selectedPlayer?.member_id],
    queryFn: () => service.getRecentActivity(selectedPlayer?.member_id || ''),
    enabled: Boolean(selectedPlayer?.member_id),
  });

  useEffect(() => {
    if (!selectedPlayer && players.length > 0) {
      setSelectedPlayer(players[0]);
    }
  }, [players, selectedPlayer]);

  const levelLabels = language === 'no'
    ? {
        level_1: 'Nivå 1',
        level_2: 'Nivå 2',
        level_3: 'Nivå 3',
        level_4: 'Nivå 4',
      }
    : {
        level_1: 'Level 1',
        level_2: 'Level 2',
        level_3: 'Level 3',
        level_4: 'Level 4',
      };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-foreground">
        {t('rankings.title')}
      </h1>

      <Card>
        <CardHeader>
          <CardTitle>{t('rankings.overview')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>{t('rankings.player')}</TableHead>
                <TableHead>{language === 'no' ? 'Nivå' : 'Level'}</TableHead>
                <TableHead>{t('rankings.rating')}</TableHead>
                <TableHead>Δ</TableHead>
                <TableHead>{t('rankings.deviation')}</TableHead>
                <TableHead>{language === 'no' ? 'Pålitelighet' : 'Confidence'}</TableHead>
                <TableHead>{language === 'no' ? 'Kamper' : 'Matches'}</TableHead>
                <TableHead>{language === 'no' ? 'Sist aktiv' : 'Last Active'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {players.map((p, idx) => {
                const reliabilityPercent = Math.round(
                  Math.max(0, Math.min(0.99, Number(p.reliability ?? 0))) * 100,
                );
                const ratingDelta = Number(p.last_rating_delta ?? 0);
                const deltaIcon = ratingDelta > 0 ? (
                  <TrendingUp className="h-4 w-4 text-emerald-600" />
                ) : ratingDelta < 0 ? (
                  <TrendingDown className="h-4 w-4 text-rose-600" />
                ) : (
                  <Minus className="h-4 w-4 text-muted-foreground" />
                );
                const ratingDeltaDisplay = Math.abs(ratingDelta) < 0.05
                  ? '0.0'
                  : `${ratingDelta > 0 ? '+' : ''}${ratingDelta.toFixed(1)}`;
                const lastActive = p.last_activity_at
                  ? formatDistanceToNow(new Date(p.last_activity_at), { addSuffix: true })
                  : '—';
                const isSelected = selectedPlayer?.player_id === p.player_id;

                return (
                  <TableRow
                    key={p.player_id}
                    onClick={() => {
                      setSelectedPlayer(p);
                      setDrawerPlayer(convertToPlayerStats(p));
                      setDrawerOpen(true);
                    }}
                    className={`cursor-pointer hover:bg-muted/50 ${isSelected ? 'bg-muted/70' : ''}`}
                    data-selected={isSelected}
                  >
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-foreground">{p.name}</span>
                        {p.phone && (
                          <span className="text-xs text-muted-foreground">{p.phone}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{levelLabels[p.level] ?? p.level}</Badge>
                    </TableCell>
                    <TableCell>{Math.round(p.rating)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {deltaIcon}
                        <span className="text-sm font-medium">{ratingDeltaDisplay}</span>
                      </div>
                    </TableCell>
                    <TableCell>{Math.round(p.rating_deviation)}</TableCell>
                    <TableCell>{`${reliabilityPercent}%`}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span>{p.matches_played}</span>
                        {p.tournament_matches > 0 && (
                          <span className="text-xs text-muted-foreground">
                            {(language === 'no' ? 'Turneringer' : 'Tournaments')}: {p.tournament_matches}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{lastActive}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {selectedPlayer && (
        <Card>
          <CardHeader>
            <CardTitle>
              {t('rankings.recentActivity')}
              {selectedPlayer?.name ? ` — ${selectedPlayer.name}` : ''}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedPlayer.member_id ? (
              <p className="text-muted-foreground">
                {language === 'no'
                  ? 'Aktivitet vises når spilleren er koblet til en medlemskonto.'
                  : 'Activity is available once the player profile is linked to a member account.'}
              </p>
            ) : activity.length === 0 ? (
              <p className="text-muted-foreground">{t('rankings.noActivity')}</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('rankings.date')}</TableHead>
                    <TableHead>{t('rankings.time')}</TableHead>
                    <TableHead>{t('rankings.event')}</TableHead>
                    <TableHead>{t('rankings.location')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activity.map((item) => {
                    const eventDate = item.event_time
                      ? new Date(`${item.event_date}T${item.event_time}`)
                      : new Date(item.event_date);
                    return (
                      <TableRow
                        key={`${item.event_id}-${item.event_date}-${item.event_time}`}
                      >
                        <TableCell>{format(eventDate, 'PP')}</TableCell>
                        <TableCell>{format(eventDate, 'p')}</TableCell>
                        <TableCell>{item.title}</TableCell>
                        <TableCell>{item.location || '—'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Complete Player Rankings */}
      <div className="mt-8">
        <h2 className="text-2xl font-bold text-foreground mb-4">
          {language === 'no' ? 'Alle Spillere' : 'All Players'}
        </h2>
        <Leaderboard />
      </div>

      {/* Player Details Drawer */}
      <PlayerDrawer
        player={drawerPlayer}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
}
